export const fmt = (n) => n.toLocaleString('en-US')

export function moneyCompact(n) {
  if (n >= 1000000) {
    const m = n / 1000000
    return '$' + (m % 1 === 0 ? m.toFixed(0) : m.toFixed(1).replace(/\.0$/, '')) + 'M'
  }
  if (n >= 10000) {
    const k = n / 1000
    return '$' + (k % 1 === 0 ? k.toFixed(0) : k.toFixed(1).replace(/\.0$/, '')) + 'K'
  }
  return '$' + fmt(n)
}

export function moneyExact(n) {
  if (n >= 1000000) return moneyCompact(n)
  return '$' + fmt(n)
}

const BURN_MIN = 20000
const BURN_MAX = 5000000

// The LiquidBrain flat price only steps every $20K of reported spend — $20K
// of metered spend maps to a $10K flat price, $40K to $20K, and so on
// (spend/2, banded). BAND_SPEND is deliberately set equal to BURN_MIN, so
// the slider's very first position ($20K) lands exactly on a band start —
// not partway through one — and shows the true minimum ratio, not something
// already partway to the next jump.
//
// Because price is a step function of spend (constant within a band, then
// jumping at each $20K boundary), the IMPLIED margin customers see isn't a
// single number — it's a sawtooth that resets to BAND_SPEND/BAND_PRICE
// (2:1) at the start of every band and climbs to just under 2x that (~4:1)
// at the top of a band, before dropping back to 2:1 at the next boundary.
// Changing BAND_PRICE (or BAND_SPEND) moves both ends of that range
// together — don't hand-tune the range's ends independently.
const BAND_SPEND = 20000
const BAND_PRICE = 10000

function flatPriceFor(spend) {
  const band = Math.floor(spend / BAND_SPEND)
  return Math.max(BAND_PRICE, band * BAND_PRICE)
}

// The slider itself runs on a 0–100 "percent" domain, not directly on
// dollars — most teams' real spend lives in the $20K–$1M range, so that
// range gets 60% of the physical track (fine control), and the $1M–$5M
// enterprise tail is compressed into the remaining 40% (coarse, fast).
// A linear $20K-per-step slider would give a $20K→$40K move (a big deal for
// most teams) the same few pixels of travel as a $1.00M→$1.02M move (noise
// at that scale) — this two-segment scale is what makes it not do that.
const SLIDER_MID_SPEND = 1000000
const SLIDER_MID_PCT = 60

// Rounds only to the nearest $1K for a clean display — NOT to the $20K
// bands. The $20K/$10K banding is a pricing-tier concept (flatPriceFor,
// below) and must stay decoupled from how finely the reported spend itself
// can move, or every drag would snap straight from $20K to $40K with
// nothing reachable in between.
const SPEND_DISPLAY_STEP = 1000

export function percentToSpend(pct) {
  const raw = pct <= SLIDER_MID_PCT
    ? BURN_MIN + (SLIDER_MID_SPEND - BURN_MIN) * (pct / SLIDER_MID_PCT)
    : SLIDER_MID_SPEND + (BURN_MAX - SLIDER_MID_SPEND) * ((pct - SLIDER_MID_PCT) / (100 - SLIDER_MID_PCT))
  return Math.max(BURN_MIN, Math.round(raw / SPEND_DISPLAY_STEP) * SPEND_DISPLAY_STEP)
}

export function spendToPercent(spend) {
  return spend <= SLIDER_MID_SPEND
    ? ((spend - BURN_MIN) / (SLIDER_MID_SPEND - BURN_MIN)) * SLIDER_MID_PCT
    : SLIDER_MID_PCT + ((spend - SLIDER_MID_SPEND) / (BURN_MAX - SLIDER_MID_SPEND)) * (100 - SLIDER_MID_PCT)
}

// 12-month relative-shape templates for the "today" (metered) chart — hand
// authored, arbitrary units. Normalized below to a mean of exactly 1, so
// `spend * shape[i]` always averages back to `spend` across the year
// regardless of which template gets picked. Kept to 10 fixed templates
// (pre-compiled, not randomly generated per render) and picked deterministically
// from the spend band, so the same slider value always renders the same chart.
const RAW_TEMPLATE_SHAPES = [
  [0.9, 0.95, 1.4, 0.85, 0.9, 1.6, 0.8, 0.85, 1.3, 0.8, 0.9, 0.85], // two mid-year pushes
  [0.85, 1.15, 0.9, 1.35, 0.8, 1.1, 0.9, 1.3, 0.85, 1.05, 0.9, 1.25], // choppy, no trend
  [1.3, 0.9, 0.85, 0.8, 0.85, 0.9, 0.85, 0.8, 0.85, 0.9, 1.3, 1.6], // launch month + year-end crunch
  [1.0, 1.05, 0.95, 1.6, 0.9, 0.95, 1.0, 1.05, 0.9, 0.95, 1.0, 1.05], // one big spike, otherwise flat
  [1.6, 1.4, 1.1, 0.9, 0.8, 0.75, 0.75, 0.8, 0.9, 1.0, 1.1, 1.3], // front-loaded, tapering off
  [0.85, 1.5, 0.9, 0.85, 1.4, 0.9, 0.85, 1.3, 0.9, 0.85, 1.2, 0.95], // recurring monthly spikes
  [0.95, 1.0, 1.05, 0.95, 1.0, 1.05, 1.5, 0.9, 0.95, 1.0, 1.05, 0.9], // single mid-summer spike
  [1.1, 0.95, 1.05, 0.9, 1.4, 0.95, 1.05, 0.9, 1.0, 1.5, 0.9, 1.0], // two spikes, mild noise elsewhere
  [0.8, 0.85, 0.9, 1.6, 1.5, 0.9, 0.85, 0.8, 0.85, 0.9, 0.95, 1.4], // Q2 double-push + year-end
  [1.0, 0.9, 1.1, 0.95, 1.05, 0.9, 1.1, 0.95, 1.05, 1.4, 1.5, 0.9], // flattish with a late-year pair
]

const TEMPLATES = RAW_TEMPLATE_SHAPES.map((shape) => {
  const mean = shape.reduce((a, b) => a + b, 0) / shape.length
  const normalized = shape.map((v) => v / mean)
  const variance = normalized.reduce((a, v) => a + (v - 1) ** 2, 0) / normalized.length
  return { shape: normalized, stddev: Math.sqrt(variance) }
})

// Deterministic pick, not random — cycling through the 10 templates by spend
// band gives visual variety as the slider moves without ever reshuffling the
// chart for a spend value the user has already landed on.
function templateFor(spend) {
  const idx = Math.floor(spend / BAND_SPEND) % TEMPLATES.length
  return TEMPLATES[idx]
}

// Real per-month dollar figures whose average is exactly `spend`. A month is
// flagged a "spike" only past one standard deviation above the mean, instead
// of an arbitrary fixed cutoff — keeps the peak proportional to how much
// this particular spend level actually varies, not a fixed multiple.
export function monthlyBreakdown(spend) {
  const { shape, stddev } = templateFor(spend)
  const spikeThreshold = spend * (1 + stddev)
  return shape.map((m) => {
    const value = Math.round(spend * m)
    return { value, spike: value > spikeThreshold }
  })
}

export function computeSavings(spend) {
  const months = monthlyBreakdown(spend)
  const values = months.map((m) => m.value)
  const low = Math.min(...values)
  const high = Math.max(...values)
  const flat = flatPriceFor(spend)
  return {
    months,
    todayRange: moneyCompact(low) + ' – ' + moneyCompact(high),
    todayBody: 'You budget ' + moneyCompact(spend) + '. The meter decides the rest — spikes land in your least shipping weeks.',
    lbPrice: moneyExact(flat),
  }
}

// The <input type="range"> itself runs on this 0–100 percent domain (see
// percentToSpend above). `step: 0.1` gives ~$1.5K–$10K of real spend per raw
// slider step across the whole range — fine enough that dragging feels
// continuous and doesn't visibly jump; the $50K/$10K price banding
// (flatPriceFor) is a separate, coarser concept layered on top of this.
export const SLIDER_DEFAULTS = { min: 0, max: 100, step: 0.1, initial: 0 }
